import { useForm } from 'react-hook-form'
import {
  forwardRef,
  type ForwardedRef,
  useCallback,
  useImperativeHandle,
} from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'

import KinerjaDropdownGroup from 'app/KinerjaPage/components/KinerjaDropdown/KinerjaDropdownGroup'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import {
  skpStatusPerencanaanFinal,
  skpStatusWaitingApproval,
  tugasPokok,
  tugasPokokPamong,
} from 'app/KinerjaPage/constants'
import PanelKinerjaCompact from 'app/KinerjaPage/components/PanelKinerjaCompact'
import useSKP from 'app/KinerjaPage/hooks/useSKP'
import PanelKinerja from 'app/KinerjaPage/components/PanelKinerja'

import type {
  KinerjaPerencanaanTabBaseProps,
  KinerjaSelection,
  SKPTabHandler,
  UpdateSKPPayload,
} from 'types/kinerja'

import ImgBadgeStarRibbonPro from 'assets/icons/profil-kompetensi/img-badge-star-ribbon-pro.svg'

const filterEmptyValues = (value: KinerjaSelection) => value.id !== 0

interface PerencanaanTabTugasPokokProps extends KinerjaPerencanaanTabBaseProps {
  customHeader?: () => JSX.Element
  customStickyFooter?: () => JSX.Element
  hideFooter?: boolean
  editReviewData?: boolean
  adjustmentInfoText?: string
  showCheckButton?: boolean
  onChange?: (values: KinerjaSelection[]) => void
  className?: string
}

const TabTugasPokok = (
  {
    skp,
    isSubmitting = false,
    reviewMode = false,
    onClickNext = () => {},
    onClickSave = () => {},
    editReviewData = false,
    onChange = () => {},
    adjustmentInfoText,
    showCheckButton,
    customHeader,
    customStickyFooter,
    hideFooter = false,
    className,
  }: PerencanaanTabTugasPokokProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { getChangesTugasTambahanByIndex } = useSKP(skp)
  const { contentTugasTambahan, getTugasTambahanById } = useContentContext()

  // Check if the user is a Pamong based on their role
  // otherwise, we assume they are a Guru
  // (since this component should be only for Guru and Pamong)
  const isPamong = skp.tipe === 'SPNF'

  const initValues =
    (editReviewData || reviewMode
      ? skp?.reviewTugasTambahan
      : skp?.tugasTambahan) ?? []
  const { setValue, handleSubmit, watch } = useForm<{
    tugasTambahan: KinerjaSelection[]
  }>({
    defaultValues: {
      tugasTambahan: initValues,
    },
  })

  const { tugasTambahan } = watch()

  const isWaitingApproval =
    skpStatusWaitingApproval.includes(skp.status) && !reviewMode

  const handleChange = (values: KinerjaSelection[]) => {
    setValue('tugasTambahan', values.filter(filterEmptyValues))
    onChange(values.filter(filterEmptyValues))
  }

  const generatePayload = useCallback((): UpdateSKPPayload => {
    return {
      id: skp.id,
      tugasTambahan: !tugasTambahan.length
        ? ([{}] as KinerjaSelection[]) // Required default value for BE validation
        : tugasTambahan.filter(filterEmptyValues),
    }
  }, [skp, tugasTambahan])

  const onSubmit = () => {
    onClickNext(generatePayload())
  }

  const onSaveDraft = useCallback(() => {
    onClickSave(generatePayload())
  }, [onClickSave, generatePayload])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  const selectedTugasPokok = isPamong ? tugasPokokPamong : tugasPokok

  const tugasTambahanDropdownProps = (i: number) => {
    return isPamong
      ? {
          number: `${selectedTugasPokok.length + i + 1}`,
          label:
            'Terlaksananya penugasan tambahan dalam mendukung pembelajaran yang bermutu untuk semua pada satuan pendidikan nonformal',
          numberingType: 'default' as const,
        }
      : {
          number: `${selectedTugasPokok.length + 1}.${i + 1}`,
          label: 'Guru melaksanakan tugas tambahan (opsional)',
          numberingType: 'group' as const,
        }
  }

  const teacherLabel = isPamong ? 'Pamong' : 'Guru'
  const originalAdjustmentLabel = `Perencanaan ${teacherLabel}`

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={clsx('p-9', className)}>
        {customHeader ? (
          customHeader()
        ) : (
          <div className="flex gap-3 items-center">
            <ImgBadgeStarRibbonPro className="w-16 h-16" />
            <div className="flex flex-col gap-1">
              <Text variant="heading-md">Pelaksanaan Tugas Pokok</Text>
              <Text color="subdued">
                Berikut tugas keseharian dan tugas tambahan Anda sebagai{' '}
                {isPamong ? 'Pamong' : 'Guru'} di satuan pendidikan. Anda tidak
                perlu unggah Bukti Dukung.
              </Text>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6 mt-6">
          {selectedTugasPokok.map((tugas, i) => (
            <PanelKinerjaCompact
              key={`tugasPokok-${i}`}
              number={i + 1}
              rhk={tugas}
            />
          ))}

          {isWaitingApproval ? (
            initValues.map(({ id }, i) => {
              const kinerja = getTugasTambahanById(id)

              return (
                <PanelKinerja
                  key={`tugasTambahan-${i}`}
                  data-testid={`tugasTambahan-${i}`}
                  label=""
                  kinerja={kinerja}
                  showDetails={false}
                  {...tugasTambahanDropdownProps(i)}
                />
              )
            })
          ) : skpStatusPerencanaanFinal.includes(skp.status) ? (
            initValues.map((_, i) => {
              const { originalKinerja, kinerja } =
                getChangesTugasTambahanByIndex(i)

              return (
                <PanelKinerja
                  key={`tugasTambahan-${i}`}
                  data-testid={`tugasTambahan-${i}`}
                  label=""
                  originalKinerja={originalKinerja}
                  kinerja={isWaitingApproval ? originalKinerja : kinerja}
                  showDetails={false}
                  showChange
                  originalAdjustmentLabel={originalAdjustmentLabel}
                  reviewAdjustmentLabel="Penyesuaian dari Kepala Sekolah"
                  {...tugasTambahanDropdownProps(i)}
                />
              )
            })
          ) : (
            <KinerjaDropdownGroup
              skp={skp}
              kinerjaType="tugasTambahan"
              offset={selectedTugasPokok.length}
              showDetails={false}
              content={contentTugasTambahan}
              kinerja={initValues}
              onChange={handleChange}
              allowDeleteSingleValue={true}
              defaultUnselectedTargetQty="-"
              adjustmentInfoText={adjustmentInfoText}
              hideAdjustmentLabel
              showCheckButton={showCheckButton}
              showChanges={editReviewData || reviewMode}
              {...(editReviewData
                ? {
                    originalKinerja: initValues,
                  }
                : {})}
              {...tugasTambahanDropdownProps(0)}
            />
          )}
        </div>
      </div>

      {!hideFooter && (
        <StickyFooter>
          {customStickyFooter ? (
            customStickyFooter()
          ) : (
            <>
              {!skpStatusPerencanaanFinal.includes(skp.status) &&
                !isWaitingApproval && (
                  <Button
                    size="lg"
                    color="white"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    onClick={onSaveDraft}
                    data-testid="btn-save-draft"
                  >
                    Simpan draf
                  </Button>
                )}
              <Button
                size="lg"
                color="black"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                type="submit"
                data-testid="btn-next"
              >
                Ke Praktik Kinerja
              </Button>
            </>
          )}
        </StickyFooter>
      )}
    </form>
  )
}

export default forwardRef<SKPTabHandler, PerencanaanTabTugasPokokProps>(
  TabTugasPokok
)
